import {
  makeRunSetFiltersSelector,
  makeRunSetGroupSelectionsSelector,
  makeRunSetSelector,
  makeRunSetSortSelector,
} from '../../components/RunSelector/runSetSelectors';
import type {RootState, ThunkResult} from '../../types/redux';
import {rootFilterSingle} from '../../util/filters';
import type {RunSetConfig} from '../../util/section';
import {EMPTY_ALL_SELECTIONS} from '../../util/selectionmanager';
import {type Ref as RunSetRef} from '../views/runSet/types';
import {type Ref as TempSelectionsRef} from '../views/tempSelections/types';
import {setCreateSweepArgs} from './actions';

const selectTempSelections = (ref: TempSelectionsRef) => (state: RootState) =>
  state.views.parts[ref.type][ref.id];

/**
 * Reads the run set and temp selections from Redux state at dispatch time,
 * assembles the RunSetConfig, and stores it for the Create Sweep page.
 *
 * This avoids the component needing to subscribe to the full assembled run set
 * (via useRunSet), which causes unnecessary re-renders whenever any run set
 * field changes. The data is only needed at the moment the button is clicked.
 */
export function createSweepWithRunSetArgs(
  runSetRef: RunSetRef,
  tempSelectionsRef: TempSelectionsRef,
  singleRunName?: string
): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();

    const runSet = makeRunSetSelector(runSetRef)(state);
    const groupSelections = makeRunSetGroupSelectionsSelector(runSetRef)(state);
    const sort = makeRunSetSortSelector(runSetRef)(state);

    let runSetConfig: RunSetConfig;
    if (singleRunName) {
      // for run set to target this specific run, we must override all search,
      // grouping, filters, and selections (aka visibility or "eyeballs")
      runSetConfig = {
        ...runSet,
        expandedRowAddresses: groupSelections.expandedRowAddresses,
        sort,
        search: {query: ''},
        grouping: [],
        selections: EMPTY_ALL_SELECTIONS,
        filters: rootFilterSingle({
          key: {section: 'run', name: 'name'},
          op: '=',
          value: singleRunName,
        }),
      };
    } else {
      const filters = makeRunSetFiltersSelector(runSetRef)(state);
      runSetConfig = {
        ...runSet,
        grouping: groupSelections.grouping,
        selections: groupSelections.selections,
        expandedRowAddresses: groupSelections.expandedRowAddresses,
        sort,
        filters,
      };
    }

    const tempSelections = selectTempSelections(tempSelectionsRef)(state);
    dispatch(setCreateSweepArgs({runSet: runSetConfig, tempSelections}));
  };
}
